import { inject } from '@angular/core';
import { catchError, EMPTY, switchMap, throwError } from 'rxjs';
import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';

/**
 * utilities
 */
import { AppHttpUtils } from '../../../utilities';

/**
 * models
 */
import { AppHttpErrorResponse } from '../../../models/httpErrorResponse/app-http-error-response';

/**
 * services
 */
import { AUTH_SESSION_MANAGEMENT } from '@identity/authentication';

export const RETRIED_AFTER_REFRESH_CONTEXT = new HttpContextToken<boolean>(() => false);

/**
 * Error HTTP Interceptor
 * 
 * @function errorInterceptor
 * @description Handles HTTP errors and manages authentication token refresh flow.
 * Automatically retries failed requests after token refresh for 401 errors.
 * Manages force refresh token context and prevents infinite retry loops.
 * 
 * @param {HttpRequest<any>} req - The outgoing HTTP request
 * @param {HttpHandlerFn} next - The next interceptor in the chain
 * @returns {Observable<HttpEvent<any>>} The HTTP response with error handling
 * 
 * @example
 * ```typescript
 * // Automatically handles 401 errors by refreshing token and retrying
 * // GET /api/users -> 401 -> Refresh Token -> Retry Request -> Success
 * 
 * // Force refresh token before request
 * this.http.get('/api/users', {
 *   context: new HttpContext().set(FORCE_REFRESH_TOKEN_CONTEXT, true)
 * }).subscribe();
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  
  const _authSessionManagement = inject(AUTH_SESSION_MANAGEMENT);

  // Check if request has bypass app request header
  const _isHasBypassAppRequest = AppHttpUtils.isHasBypassAppRequest(req);

  // check if request has bypass error header
  const _isHasBypassErrorResponse = AppHttpUtils.isHasBypassErrorResponse(req); 

  // handle error response
  const _next = next(req).pipe(
    catchError((response: HttpErrorResponse) => {
      // convert error to app error
      const appError = new AppHttpErrorResponse(response);
      return throwError(() => appError);
    })
  );

  // bypass request
  if (_isHasBypassAppRequest || _isHasBypassErrorResponse) {
    return _next;
  }
  
  // handle 401 error
  return _next.pipe(
    catchError((response: AppHttpErrorResponse) => {
      
      // check if error is a 401
      if (response.status !== 401) return throwError(() => response);
      
      const alreadyRetried = req.context.get(RETRIED_AFTER_REFRESH_CONTEXT) === true;
      const hasRefresh = !!_authSessionManagement.refreshToken;
      const isRefreshEndpoint = req.url.includes('/login/refresh') || req.url.includes('/refresh');

      if (isRefreshEndpoint || alreadyRetried || !hasRefresh) {
        _authSessionManagement.logout().subscribe();
        return EMPTY;
      }

      // refresh token
      return _authSessionManagement.refreshAccessTokenShared().pipe(
        switchMap((newToken) => {
          const retriedReq = req.clone({
            context: req.context.set(RETRIED_AFTER_REFRESH_CONTEXT, true),
            setHeaders: { Authorization: `Bearer ${newToken}` },
          });
          return next(retriedReq);
        }),
        catchError(() => {
          _authSessionManagement.logout().subscribe();
          return EMPTY;
        })
      );

    })
  );

};
